import {
  Tags,
  TextComponentNameLevel1,
} from "Components/TextContext/textConstants";
import {
  ComponentCollection,
  Data,
  DittoCoverage,
  DittoCoverageMapped,
} from "Components/TextContext/textInterfaces";
import {
  AkaFamilyAccidentCoverage,
  AnimalCoverage,
  CabinCoverage,
  ChildCoverage,
  HealthCoverage,
  HouseContentsCoverage,
  HouseCoverage,
  InsuranceCoverage,
  TravelCoverage,
} from "Store/interfaces/products/coverage";

import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { getCollectionOfDittoComponents } from "Components/TextContext";

export const getDittoCoverageTexts = (
  product: ProductBackendName,
): ComponentCollection | undefined => {
  switch (product) {
    case ProductBackendName.ACCIDENT:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.ACCIDENT_COVERAGES,
      );
    case ProductBackendName.AKA_CHILD:
      return getCollectionOfDittoComponents(TextComponentNameLevel1.AKA_CHILD);
    case ProductBackendName.AKA_FAMILY_ACCIDENT:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.AKA_FAMILY_ACCIDENT,
      );
    case ProductBackendName.AKA_HEALTH:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.AKA_HEALTH_COVERAGES,
      );
    case ProductBackendName.AKA_HOUSE_CONTENTS:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.AKA_HOUSE_CONTENTS_COVERAGES,
      );
    case ProductBackendName.AKA_STUDENT:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.AKA_STUDENT_COVERAGES,
      );
    case ProductBackendName.AKA_TRAVEL:
      return getCollectionOfDittoComponents(TextComponentNameLevel1.AKA_TRAVEL);
    case ProductBackendName.ANIMAL:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.ANIMAL_COVERAGES_NEW,
      );
    case ProductBackendName.CABIN:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.CABIN_COVERAGES,
      );
    case ProductBackendName.CHILD:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.CHILD_COVERAGES,
      );
    case ProductBackendName.CHILD_HEALTH:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.CHILD_HEALTH_COVERAGES,
      );
    case ProductBackendName.HEALTH:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.HEALTH_COVERAGES,
      );
    case ProductBackendName.HOUSE:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.HOUSE_COVERAGES,
      );
    case ProductBackendName.HOUSE_CONTENTS:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.HOUSE_CONTENTS_COVERAGES,
      );
    case ProductBackendName.HUSEIERNE_HOUSE_CONTENTS:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.HUSEIERNE_HOUSE_CONTENTS_COVERAGE,
      );
    case ProductBackendName.MOPED:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.MOPED_COVERAGES,
      );
    case ProductBackendName.PREGNANT_AND_NEWBORN:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.PREGNANT_AND_NEWBORN_COVERAGES,
      );
    case ProductBackendName.RENTAL:
      return getCollectionOfDittoComponents(
        TextComponentNameLevel1.RENTAL_COVERAGES,
      );
    default:
      return undefined;
  }
};

export const getIncludedCoverages = (
  coverage: InsuranceCoverage,
  coverageTexts?: ComponentCollection,
): DittoCoverageMapped[] => {
  if (coverageTexts === undefined) {
    throw new Error(`Can't find text components for ${coverage}`);
  }

  const includedCoverages = filterIncludedCoverages(coverage, coverageTexts);

  return mapCoverages(includedCoverages);
};

export const getExcludedCoverages = (
  coverage: InsuranceCoverage,
  betterCoverages?: InsuranceCoverage[],
  coverageTexts?: ComponentCollection,
): DittoCoverageMapped[] | [] => {
  if (coverageTexts === undefined) {
    throw new Error(`Can't find text components for ${coverage}`);
  }

  if (betterCoverages === undefined) {
    return [];
  }

  const excludedCoverages = filterExcludedCoverages(
    coverage,
    betterCoverages,
    coverageTexts,
  );

  return mapCoverages(excludedCoverages);
};

export const getBetterInsuranceCoverage = (
  coverage: InsuranceCoverage,
): InsuranceCoverage[] | undefined => {
  const coverageGroup = mapCoverageGroup(coverage);

  if (
    coverageGroup === AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DISABILITY
  ) {
    return [
      AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DEATH_AND_DISABILITY,
      AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DEATH_AND_DISABILITY_AND_PROFESSIONAL_DISABILITY,
    ];
  }

  switch (coverageGroup) {
    case HouseCoverage.HOUSE_STANDARD:
      return [HouseCoverage.HOUSE_EXTENDED];
    case HouseContentsCoverage.HOUSE_CONTENTS_STANDARD:
      return [HouseContentsCoverage.HOUSE_CONTENTS_EXTENDED];
    case CabinCoverage.CABIN_STANDARD:
      return [CabinCoverage.CABIN_EXTENDED];
    case ChildCoverage.BASIC:
      return [ChildCoverage.EXTRA];
    case TravelCoverage.TRAVEL_STANDARD:
      return [TravelCoverage.TRAVEL_SUPER];
    case AnimalCoverage.CAT_DEATH:
      return [AnimalCoverage.CAT_VET];
    case AnimalCoverage.CAT_VET:
      return [AnimalCoverage.CAT_DEATH];
    case AnimalCoverage.DOG_DEATH_AND_INSURANCE_VALUE:
      return [AnimalCoverage.DOG_VET];
    case AnimalCoverage.DOG_VET:
      return [AnimalCoverage.DOG_DEATH_AND_INSURANCE_VALUE];
    case HealthCoverage.HEALTH_BASIS:
    case HealthCoverage.HEALTH_PLUS:
      return [HealthCoverage.HEALTH_TOP];
    case AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DEATH:
      return [
        AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DEATH_AND_DISABILITY,
      ];
    case AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DEATH_AND_DISABILITY:
    case AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DISABILITY_AND_PROFESSIONAL_DISABILITY:
      return [
        AkaFamilyAccidentCoverage.AKA_FAMILY_ACCIDENT_DEATH_AND_DISABILITY_AND_PROFESSIONAL_DISABILITY,
      ];
    default:
      return undefined;
  }
};

export enum TravelCoverageGroup {
  SUPER = "TRAVEL_SUPER",
  STANDARD = "TRAVEL_STANDARD",
}

export const mapCoverageGroup = (coverage: InsuranceCoverage): string => {
  switch (coverage) {
    case TravelCoverage.TRAVEL_STANDARD:
    case TravelCoverage.TRAVEL_COVERAGE_FOR_INSURED_ONLY:
      return TravelCoverageGroup.STANDARD;
    case TravelCoverage.TRAVEL_SUPER:
    case TravelCoverage.TRAVEL_SUPER_90_DAYS:
    case TravelCoverage.TRAVEL_SUPER_120_DAYS:
    case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY:
    case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_90_DAYS:
    case TravelCoverage.TRAVEL_SUPER_COVERAGE_FOR_INSURED_ONLY_120_DAYS:
      return TravelCoverageGroup.SUPER;

    default:
      return coverage;
  }
};

export const filterIncludedCoverages = (
  coverage: InsuranceCoverage,
  coverageTexts: ComponentCollection,
): DittoCoverage[] => {
  return Object.entries(coverageTexts)
    .filter((element) => {
      const components = element[1];

      return components[0].tags?.includes(coverage);
    })
    .sort();
};

export const filterExcludedCoverages = (
  coverage: InsuranceCoverage,
  betterCoverages: InsuranceCoverage[],
  coverageTexts: ComponentCollection,
): DittoCoverage[] => {
  return Object.entries(coverageTexts)
    .filter((element) => {
      const components = element[1];

      return betterCoverages.some((coverageToExclude) => {
        return (
          components[0].tags?.includes(coverageToExclude) &&
          !components[0].tags?.includes(coverage)
        );
      });
    })
    .sort();
};

export const mapCoverages = (
  coverages: DittoCoverage[],
): DittoCoverageMapped[] => {
  return coverages.map((item) => {
    return item[1].reduce(
      (acc: any, component: Data) => {
        if (component.name.includes("caption")) {
          acc = { ...acc, caption: { ...component } };
        } else if (component.name.includes("description")) {
          acc = { ...acc, description: { ...component } };
        }

        return acc;
      },
      { caption: {}, description: {} },
    );
  });
};

export const isComponentWithMarkdown = (component: Data): boolean => {
  if (component.tags === undefined) {
    return false;
  }
  return component.tags.includes(Tags.MARKDOWN);
};
